import { useOrg } from "./useOrg";
import { createContext, useContext, useState, useEffect, ReactNode } from "react";

const FAMILY_ID_STORAGE_KEY = 'vintage_client_view_family_id';

interface TenantContextValue {
  orgType: 'sfo' | 'mfo' | null;
  orgId: string | null;
  familyId: string | null;
  setFamilyId: (id: string | null) => void;
  isSFO: boolean;
  isMFO: boolean;
  isClientView: boolean;
  families: Array<{ id: string; name: string; org_id: string; meta: Record<string, unknown>; created_at: string; onboarding_status?: 'draft' | 'kyc_pending' | 'data_pending' | 'review' | 'active'; portal_access_enabled?: boolean }>;
  currentOrg: { id: string; name: string; org_type: 'sfo' | 'mfo'; base_ccy: string; tz: string; created_at: string; logo_url?: string | null; primary_color?: string | null; accent_color?: string | null; font_family?: string | null } | null;
  isLoading: boolean;
}

const TenantContext = createContext<TenantContextValue | null>(null);

/**
 * Provider component that shares tenant state across the app.
 * Wrap your app with this to enable useTenant() in child components.
 */
export function TenantProvider({ children }: { children: ReactNode }) {
  const { currentOrg, isSFO, isMFO, families, defaultFamily, isLoading } = useOrg();

  // Initialize from sessionStorage for MFO users
  const [familyIdState, setFamilyIdState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return sessionStorage.getItem(FAMILY_ID_STORAGE_KEY);
  });

  // Get familyId: MFO uses persisted state, SFO uses default family
  const familyId = isMFO ? familyIdState : defaultFamily?.id;

  // Sync familyId to sessionStorage when it changes
  useEffect(() => {
    if (!isMFO) return;
    if (familyIdState) {
      sessionStorage.setItem(FAMILY_ID_STORAGE_KEY, familyIdState);
    } else {
      sessionStorage.removeItem(FAMILY_ID_STORAGE_KEY);
    }
  }, [familyIdState, isMFO]);

  const setFamilyId = (newFamilyId: string | null) => {
    if (!isMFO) return; // SFO doesn't switch families
    setFamilyIdState(newFamilyId);
  };

  // isClientView: true when MFO user is viewing a specific family's data
  const isClientView = isMFO && !!familyId;

  const value: TenantContextValue = {
    orgType: currentOrg?.org_type || null,
    orgId: currentOrg?.id || null,
    familyId: familyId || null,
    setFamilyId,
    isSFO,
    isMFO,
    isClientView,
    families: families || [],
    currentOrg,
    isLoading,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

/**
 * Hook to access tenant context (org + family scope).
 * Must be used within a TenantProvider.
 */
export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
